import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ComputerDesktopIcon,
  EllipsisVerticalIcon,
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ArrowPathIcon,
  FolderOpenIcon,
  GlobeAltIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Dropdown } from '../components/ui';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';

interface Website {
  id: string;
  name: string;
  domain: string;
  published: boolean;
}

interface ProgressStep {
  id: number;
  label: string;
  duration: number;
  status: 'pending' | 'completed' | 'error';
  elapsed?: number;
}

const Websites: React.FC = () => {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDomainModal, setShowDomainModal] = useState<boolean>(false);
  const [selectedWebsite, setSelectedWebsite] = useState<Website | null>(null);
  const [customDomain, setCustomDomain] = useState<string>('');
  const [newWebsiteName, setNewWebsiteName] = useState<string>('');
  const [createError, setCreateError] = useState<string>('');
  const [creatingWebsite, setCreatingWebsite] = useState<boolean>(false);
  const [updatingDomain, setUpdatingDomain] = useState<boolean>(false);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState<string>('');
  const [savingName, setSavingName] = useState<boolean>(false);
  const [editNameError, setEditNameError] = useState<string>('');
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);

  useEffect(() => {
    fetchWebsites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API_URL}/websites`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      setWebsites(response.data || []);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching websites:', err);
      setError('Failed to load websites');
      setLoading(false);
    }
  };

  const handleCreateWebsite = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    const nameRegex = /^[a-z0-9-]+$/;
    if (!nameRegex.test(newWebsiteName)) {
      setCreateError('Website name must contain only lowercase letters, numbers, and hyphens');
      return;
    }
    setCreatingWebsite(true);
    try {
      await axios.post(`${API_URL}/websites`, { name: newWebsiteName }, {
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
      });
      setShowCreateModal(false);
      setNewWebsiteName('');
      fetchWebsites();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create website');
    } finally {
      setCreatingWebsite(false);
    }
  };

  const runProgressSteps = async (
    steps: { label: string; duration: number }[],
    apiCall: () => Promise<void>,
    onDone: () => void
  ) => {
    setProgressSteps(steps.map((s, idx) => ({ ...s, status: 'pending', id: idx })));
    setCurrentStepIndex(0);
    setShowProgressModal(true);

    try {
      for (let i = 0; i < steps.length; i++) {
        setCurrentStepIndex(i);
        const startTime = Date.now();
        if (i === steps.length - 1) {
          await apiCall();
        } else {
          await new Promise(resolve => setTimeout(resolve, steps[i].duration));
        }
        const elapsed = Date.now() - startTime;
        setProgressSteps(prev => prev.map((s, idx) => idx === i ? { ...s, status: 'completed', elapsed } : s));
      }
      setTimeout(() => { setShowProgressModal(false); onDone(); }, 800);
    } catch (err: any) {
      setProgressSteps(prev => prev.map((s, idx) => idx === currentStepIndex ? { ...s, status: 'error' } : s));
      setTimeout(() => { setShowProgressModal(false); toast('Operation failed: ' + (err.response?.data?.error || err.message), 'error'); }, 1500);
    }
  };

  const isDefaultManagedDomain = (domain: string): boolean =>
    /^.+\.[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.edj-labs\.com$/i.test(domain);

  const handlePublish = async (website: Website) => {
    const isManagedDomain = website.domain && isDefaultManagedDomain(website.domain);
    const steps = isManagedDomain
      ? [{ label: 'Creating the application stack', duration: 3000 }, { label: 'Creating the DNS entry', duration: 2000 }, { label: 'Deploying website', duration: 2000 }]
      : [{ label: 'Creating the application stack', duration: 3000 }, { label: 'Deploying website', duration: 2000 }];
    await runProgressSteps(steps,
      () => axios.post(`${API_URL}/websites/${website.id}/publish`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => { fetchWebsites(); toast('Your website has been published. It may take several minutes before it is reachable online due to DNS and cache propagation.', 'success'); }
    );
  };

  const handleRedeploy = async (websiteId: string) => {
    const steps = [{ label: 'Deploying updated content', duration: 2000 }];
    await runProgressSteps(steps,
      () => axios.post(`${API_URL}/websites/${websiteId}/redeploy`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => fetchWebsites()
    );
  };

  const handleUnpublish = async (website: Website) => {
    const isManagedDomain = website.domain && isDefaultManagedDomain(website.domain);
    const steps = isManagedDomain
      ? [{ label: 'Removing application stack', duration: 2000 }, { label: 'Removing DNS entry', duration: 2000 }, { label: 'Cleaning up resources', duration: 2000 }]
      : [{ label: 'Removing application stack', duration: 2000 }, { label: 'Cleaning up resources', duration: 2000 }];
    await runProgressSteps(steps,
      () => axios.post(`${API_URL}/websites/${website.id}/unpublish`, {}, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => fetchWebsites()
    );
  };

  const handleDelete = async (website: Website) => {
    const ok = await confirm({ title: 'Delete website', message: 'Are you sure you want to delete this website? This action cannot be undone.', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    const isManagedDomain = website.domain && isDefaultManagedDomain(website.domain);
    const steps = isManagedDomain
      ? [{ label: 'Removing application stack', duration: 2000 }, { label: 'Removing DNS entry', duration: 2000 }, { label: 'Removing files', duration: 2000 }]
      : [{ label: 'Removing application stack', duration: 2000 }, { label: 'Removing files', duration: 2000 }];
    await runProgressSteps(steps,
      () => axios.delete(`${API_URL}/websites/${website.id}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      () => fetchWebsites()
    );
  };

  const handleStartEditName = (website: Website) => {
    setEditingNameId(website.id);
    setEditNameValue(website.name);
    setEditNameError('');
  };

  const handleCancelEditName = () => {
    setEditingNameId(null);
    setEditNameValue('');
    setEditNameError('');
  };

  const handleSaveName = async (website: Website) => {
    const nameRegex = /^[a-z0-9-]+$/;
    if (!nameRegex.test(editNameValue)) { setEditNameError('Only lowercase letters, numbers, and hyphens allowed'); return; }
    if (editNameValue === website.name) { handleCancelEditName(); return; }
    setSavingName(true);
    setEditNameError('');
    try {
      await axios.put(`${API_URL}/websites/${website.id}`, { name: editNameValue },
        { headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' } });
      setEditingNameId(null);
      setEditNameValue('');
      fetchWebsites();
    } catch (err: any) {
      setEditNameError(err.response?.data?.error || 'Failed to rename website');
    } finally {
      setSavingName(false);
    }
  };

  const handleOpenDomainModal = (website: Website) => {
    setSelectedWebsite(website);
    setCustomDomain(website.domain);
    setShowDomainModal(true);
  };

  const handleUpdateDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdatingDomain(true);
    try {
      await axios.put(`${API_URL}/websites/${selectedWebsite!.id}/domain`, { domain: customDomain },
        { headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' } });
      setShowDomainModal(false);
      setSelectedWebsite(null);
      setCustomDomain('');
      fetchWebsites();
    } catch (err: any) {
      toast('Failed to update domain: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setUpdatingDomain(false);
    }
  };

  const handleResetDomain = async () => {
    if (!selectedWebsite) return;
    setUpdatingDomain(true);
    try {
      await axios.post(`${API_URL}/websites/${selectedWebsite.id}/domain/reset`, {},
        { headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' } });
      setShowDomainModal(false);
      setSelectedWebsite(null);
      setCustomDomain('');
      fetchWebsites();
    } catch (err: any) {
      toast('Failed to reset domain: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setUpdatingDomain(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<ComputerDesktopIcon />}
          title="Websites"
          description="Manage your websites."
          actions={
            <button type="button" className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm" disabled>
              Create Website
            </button>
          }
        />
        <div className="grid gap-4">
          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-6 space-y-3">
            <Skeleton className="h-6 w-52" />
            <Skeleton className="h-4 w-72" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-6 space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">{error}</p></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ComputerDesktopIcon />}
        title="Websites"
        description="Manage your websites."
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2 cursor-pointer">
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Website
          </button>
        }
      />

      {websites.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-sm font-semibold text-foreground">No websites yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">You haven't created any websites yet.</p>
          <div className="mt-4">
            <button type="button" onClick={() => setShowCreateModal(true)} className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 cursor-pointer">
              Create Your First Website
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {websites.map((website) => (
            <div key={website.id} className="bg-card border border-border rounded-lg shadow-elevation-1 p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {editingNameId === website.id ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <input type="text" value={editNameValue}
                            onChange={(e) => { setEditNameValue(e.target.value); setEditNameError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(website); if (e.key === 'Escape') handleCancelEditName(); }}
                            className="min-w-[200px] px-2 py-1 border border-border rounded text-xl font-semibold text-foreground focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-opacity-50"
                            autoFocus disabled={savingName} />
                          <button onClick={() => handleSaveName(website)} disabled={savingName}
                            className="px-2 py-1 text-sm text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{savingName ? 'Saving…' : 'Save'}</button>
                          <button onClick={handleCancelEditName} disabled={savingName} className="px-2 py-1 text-sm border border-border rounded hover:bg-muted/40">Cancel</button>
                        </div>
                        {editNameError && <p className="text-xs text-red-600">{editNameError}</p>}
                      </div>
                    ) : (
                      <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <span>{website.name}</span>
                        <button onClick={() => handleStartEditName(website)} className="text-muted-foreground hover:text-muted-foreground transition-colors" title="Rename website">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
                          </svg>
                        </button>
                      </h3>
                    )}
                    <span className="text-sm text-muted-foreground">ID: <span className="font-mono">{website.id}</span></span>
                  </div>
                  <p className="text-muted-foreground mb-2">
                    <span className="font-medium">Domain:</span>{' '}
                    <a href={`https://${website.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{website.domain}</a>
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-muted-foreground">Status:</span>
                    <span className={`px-2 py-1 rounded text-sm ${website.published ? 'bg-success/10 text-success' : 'bg-muted text-foreground'}`}>
                      {website.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  {website.published && <p className="mt-2 text-xs text-muted-foreground">Unpublish this website before changing its domain.</p>}
                </div>
                <div className="flex gap-2">
                  <Dropdown
                    align="right"
                    trigger={
                      <button
                        type="button"
                        className="p-2 rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                        title="Actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                    }
                    items={[
                      ...(!website.published
                        ? [{ label: 'Publish', icon: <ArrowUpOnSquareIcon />, onSelect: () => handlePublish(website) }]
                        : [
                            { label: 'Unpublish', icon: <ArrowDownOnSquareIcon />, onSelect: () => handleUnpublish(website) },
                            { label: 'Redeploy', icon: <ArrowPathIcon />, onSelect: () => handleRedeploy(website.id) },
                          ]
                      ),
                      { label: 'Manage Files', icon: <FolderOpenIcon />, onSelect: () => navigate(`/websites/${website.id}/files`) },
                      { label: 'Edit Domain', icon: <GlobeAltIcon />, disabled: website.published, onSelect: () => { if (!website.published) handleOpenDomainModal(website); } },
                      { separator: true },
                      { label: 'Delete', icon: <TrashIcon />, danger: true, onSelect: () => handleDelete(website) },
                    ]}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-2xl w-full mx-4">
            <h2 className="text-xl font-semibold text-foreground mb-6">Processing...</h2>
            <div className="space-y-4">
              {progressSteps.map((step, idx) => (
                <div key={step.id} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6">
                    {step.status === 'completed' ? (
                      <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ) : step.status === 'error' ? (
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    ) : idx === currentStepIndex ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-border"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`text-foreground ${idx === currentStepIndex ? 'font-medium' : ''}`}>{step.label}</span>
                    {step.status === 'completed' && step.elapsed && (
                      <span className="text-muted-foreground text-sm ml-2">{(step.elapsed / 1000).toFixed(1)}s</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
      document.body
      )}

      {/* Create Website Modal */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-foreground mb-4">Create New Website</h2>
            <form onSubmit={handleCreateWebsite}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">Website Name</label>
                <input type="text" value={newWebsiteName} onChange={(e) => setNewWebsiteName(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  placeholder="my-awesome-site" required />
                <p className="text-xs text-muted-foreground mt-1">Only lowercase letters, numbers, and hyphens allowed</p>
              </div>
              {createError && (<div className="mb-4 p-3 bg-destructive/10 border border-red-200 rounded"><p className="text-sm text-red-600">{createError}</p></div>)}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowCreateModal(false); setNewWebsiteName(''); setCreateError(''); }}
                  className="px-4 py-2 border border-border rounded hover:bg-muted/40 transition-colors" disabled={creatingWebsite}>Cancel</button>
                <button type="submit" className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary rounded hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
                  disabled={creatingWebsite}>{creatingWebsite ? 'Creating...' : 'Create Website'}</button>
              </div>
            </form>
          </div>
        </div>,
      document.body
      )}

      {/* Domain Configuration Modal */}
      {showDomainModal && selectedWebsite && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Configure Domain</h2>
              <button type="button" onClick={handleResetDomain} className="p-1.5 rounded-full border border-border hover:bg-muted/40 transition-colors" title="Reset to default domain" disabled={updatingDomain}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m0 0A7.5 7.5 0 1112 19.5 7.5 7.5 0 016.582 9H4" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleUpdateDomain}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">Domain</label>
                <input type="text" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  placeholder="example.com or subdomain.example.com" required />
                <p className="text-xs text-muted-foreground mt-1">Enter your custom domain or use the default assigned domain</p>
              </div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-700"><span className="font-medium">Note:</span> If you change to a custom domain, make sure you own it in the Domains section and configure a DNS A record yourself pointing to 79.137.79.153. Custom domains do not create DNS records automatically.</p>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowDomainModal(false); setSelectedWebsite(null); setCustomDomain(''); }}
                  className="px-4 py-2 border border-border rounded hover:bg-muted/40 transition-colors" disabled={updatingDomain}>Cancel</button>
                <button type="submit" className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary rounded hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
                  disabled={updatingDomain}>{updatingDomain ? 'Updating...' : 'Update Domain'}</button>
              </div>
            </form>
          </div>
        </div>,
      document.body
      )}
    </div>
  );
};

export default Websites;
